import { useRef, useEffect } from "react";
import { MessageSquare, Loader2 } from "lucide-react";
import type { ConversationType } from "../../types";
import {
  useProjectMessages,
  useMentionUsers,
  type ConversationBase,
} from "../../api/conversationsApi";
import { MessageItem } from "./MessageItem";
import { MessageComposer } from "./MessageComposer";

interface Props {
  projectId: string;
  conversationType: ConversationType;
  title: string;
  currentUserId: string;
  isCeo: boolean;
  /** Do czego przypięta jest rozmowa: przedsięwzięcie (domyślnie) albo umowa ramowa. */
  base?: ConversationBase;
}

export function ConversationPanel({
  projectId,
  conversationType,
  title,
  currentUserId,
  isCeo,
  base = "projects",
}: Props) {
  const { data: messages = [], isLoading } = useProjectMessages(
    projectId,
    conversationType,
    base,
  );
  const { data: mentionUsers = [] } = useMentionUsers(
    projectId,
    conversationType,
    true,
    base,
  );
  const listRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(0);

  useEffect(() => {
    if (!listRef.current) return;
    const isNewMessage = messages.length > prevLengthRef.current && prevLengthRef.current > 0;
    listRef.current.scrollTop = listRef.current.scrollHeight;
    if (isNewMessage) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    } else {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
    prevLengthRef.current = messages.length;
  }, [messages.length]);

  return (
    <div className="flex flex-col bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden h-full min-h-[400px]">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
        <MessageSquare size={15} className="text-[var(--color-primary)]" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
          {messages.length} {messages.length === 1 ? "wiadomość" : "wiadomości"}
        </span>
      </div>

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-gray-400">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-400 dark:text-gray-600 gap-2">
            <MessageSquare size={24} strokeWidth={1.5} />
            <p className="text-xs">Brak wiadomości. Napisz pierwszą.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <MessageItem
              key={msg.id}
              message={msg}
              projectId={projectId}
              conversationType={conversationType}
              currentUserId={currentUserId}
              isCeo={isCeo}
              mentionUsers={mentionUsers}
              base={base}
            />
          ))
        )}
      </div>

      {/* Composer */}
      <div className="px-3 pb-3 pt-2 border-t border-gray-100 dark:border-gray-700">
        <MessageComposer
          projectId={projectId}
          conversationType={conversationType}
          mentionUsers={mentionUsers}
          base={base}
        />
      </div>
    </div>
  );
}
